import React, {useEffect, useState} from "react";
import Stack from "@mui/material/Stack";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {CalcSelectCar} from "./CalcSelectCar";
import {useCalcContext} from "../../contexts/CalcContext";
import useEnterEffect from "../../hooks/useEnterEffect";
import {useLocation} from "react-router-dom";
import {CalcPageSelectLocation} from "./CalcPageSelectLocation";
import {CalcPagePricing} from "./CalcPagePricing";
import {DeskingModal} from "../PricingListPage/DeskingModal";
import {DealModal} from "../PricingListPage/DealModal";
import {useModalsStore} from "../../stores/modalsStore";
import {useShallow} from "zustand/react/shallow";
import {createCashDealDirect} from "../../utils/createCashDealDirect";
import fetcher from "../../fetcher";
import {toast} from "react-toastify";


export const CalcPageInfo = () => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    let location = useLocation();
    const [isCreatingLease, setIsCreatingLease] = useState(false)
    const [isCreatingPurchase, setIsCreatingPurchase] = useState(false)
    const {
        carState,
        calcState,
        getLeadId,
        getOpportunityId,
    } = useCalcContext()

    const {
        openModal,
        hasModalOpen,
    } = useModalsStore(
        useShallow((state) => ({
            openModal: state.openModal,
            closeModal: state.closeModal,
            hasModalOpen: state.hasModalOpen,
        }))
    )

    const onCreate = async (newLeaseData) => {
        // Which deal the Quote page goes on to open. Normally the one just
        // created — but a CRM attach stores a clone and shows *that* under the
        // opportunity, so the attach redirects us to the copy. Awaited: opening
        // before it resolves is what used to strand the rep on the original.
        const opportunityId = getOpportunityId()
        let quoteDealId = newLeaseData?.id
        if (opportunityId && newLeaseData?.id) {
            try {
                const res = await fetcher.attachCrmQuote(opportunityId, newLeaseData.id)
                // No id back means an older backend — open the original rather
                // than `undefined`.
                quoteDealId = res?.data?.attached_quote_id ?? newLeaseData.id
                // Tell the person-detail view that launched this tab to refresh
                // (opener stays open; we don't close this tab like the lead flow).
                if (window.opener && !window.opener.closed &&
                    typeof window.opener.onOpportunityQuoteCreated === 'function') {
                    window.opener.onOpportunityQuoteCreated(newLeaseData)
                }
            } catch (e) {
                // The deal itself saved; only the CRM link failed, so the rep
                // still gets their worksheet.
                toast.error('Could not attach the quote to the opportunity.')
            }
        }

        if (getLeadId()) {
            window.onunload = function() {
                if (window.opener && !window.opener.closed) {
                    window.opener.onLeadCreate(newLeaseData);
                }
            };
            window.close();
        } else {
            if (isTabletOrMobile) {
                window.open(`/quotes/${quoteDealId}`, "_self")
            } else {
                openModal({
                    element: DealModal,
                    id: "DealModal",
                    props: {id: quoteDealId}
                })
            }
        }
    }

    const deskDeal = (isPurchase, isOnePay) => {
        isPurchase ? setIsCreatingPurchase(true) : setIsCreatingLease(true)

        const deskingData = structuredClone(calcState)
        delete deskingData.location
        delete deskingData.location_id

        deskingData.is_purchase = isPurchase
        deskingData.is_one_pay = isOnePay

        let mileage = deskingData.mileage
        if (['Ford', 'Lincoln'].includes(carState?.make)) {
            mileage = deskingData.mileage_alt
        }
        deskingData.mileage = mileage

        const openGrid = () => openModal({
            element: DeskingModal,
            id: "DeskingModal",
            props: {
                onSave: (data) =>  onCreate(data),
                isDeskingContext: false,
                ...deskingData,
            }
        })

        if (isPurchase && isOnePay) {
            // Cash: skip the "Quotes for…" grid entirely — scan, auto-pick the
            // cash quote, create the deal, and open the cash quote page directly.
            createCashDealDirect(deskingData, { onSave: (data) => onCreate(data), onNeedsGrid: openGrid })
                .catch(() => {})
                .finally(() => setIsCreatingPurchase(false))
            return
        }

        isPurchase ? setIsCreatingPurchase(false) : setIsCreatingLease(false)
        openGrid()
    }

    const isCalculatePath = location.pathname.split('/')[1] === 'calculate'

    useEnterEffect(function enterDeskLease(){
        if (
            calcState.base_car_id &&
            calcState.msrp &&
            calcState.cost &&
            isCalculatePath
        ) {
            if (!hasModalOpen()) {
                deskDeal(false, false)
            }
        }
    }, [calcState, hasModalOpen])

    return (
        <Stack
            style={{ width: '100%' }}
            direction={isTabletOrMobile ? 'column' : 'row'}
            alignItems="stretch"
        >
            <CalcSelectCar />
            <CalcPageSelectLocation />

            <CalcPagePricing deskDeal={deskDeal} isCreatingCash={isCreatingPurchase}/>
        </Stack>
    )
}